import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { getTrackAvailability } from '../utils/trackAccess';
import { useMediaSession } from '../hooks/useMediaSession';
import { playbackSrc, assignPlaybackSrc, isSavedOfflineSync, offlineSrcFor, offlineCopyIsStale } from '../utils/offlineStore';
import { resolveStreamLater, warmStreamUrls } from '../utils/streamUrl';
import { buildOfflinePlayRow, queueOfflinePlay, flushOfflinePlays } from '../utils/offlinePlayQueue';
import { sendNotification, sendStreamDigest } from '../utils/notify';

// ── Where a play came from ──────────────────────────────────────────────────
//
// event_source used to be read straight off window.__feelz_play_source at the
// moment the event was written, and only five places in the whole app ever set
// it: For You, Home card, artist profile, school sessions. Everything else,
// Library, Browse, an album, a playlist, Liked, search, a track page, Driving,
// landed as 'unknown'. That was 237 plays over thirty days, 13% of everything,
// sitting in one bucket with a 64% completion rate, which is to say the
// deliberate listening, the most valuable signal on the platform, was the part
// that could not be attributed.
//
// Worse, the global was sticky. It was set on a click and never cleared, so a
// play started from Library after visiting an artist page was recorded as
// artist_profile. The untagged surfaces were not only missing, they were
// quietly inflating the tagged ones.
//
// Both are fixed the same way: the source is resolved once, at playTrack,
// which the comment on that function correctly calls the one gate every path
// into playback goes through. An explicit setter still wins, but only if it
// was set in the last few seconds, which is the difference between "this click
// set it" and "something set it four pages ago". Otherwise the route the
// person is actually on decides, which is right by construction and cannot be
// forgotten by a new call site.
const SOURCE_TTL_MS = 8000;

export function sourceFromPath(path) {
  const p = String(path || '').toLowerCase().replace(/\/+$/, '') || '/';
  if (p === '/') return 'for_you';
  if (p === '/home') return 'home';
  if (p === '/browse') return 'browse';
  if (p === '/feed') return 'feed';
  if (p === '/community' || p.startsWith('/chat')) return 'community';
  if (p === '/library') return 'library';
  if (p === '/library/likes') return 'liked_songs';
  if (p === '/library/downloads') return 'downloads';
  if (p === '/library/offline') return 'offline_library';
  if (p === '/library/recent') return 'recently_played';
  if (p.startsWith('/library/playlists')) return 'playlist';
  if (p.startsWith('/album/')) return 'album';
  if (p.startsWith('/beat/')) return 'beat_page';
  if (p.startsWith('/artist/')) return 'artist_profile';
  if (p.startsWith('/track/') || p.startsWith('/t/') || p.startsWith('/a/')) return 'track_page';
  if (p.startsWith('/session/')) return 'listening_session';
  if (p.startsWith('/competition')) return 'competitions';
  // Both paths, because /sessions is canonical now and /schoolsessions is a
  // permanent alias, and a play from either is the same thing. The tag keeps
  // the old name on purpose: it is a stored value, and renaming it would
  // split this context's history in two for the sake of a cosmetic.
  // Note the order: the /session/ test above is a listening session, and
  // '/sessions' does not start with '/session/', so the two cannot collide.
  if (p.startsWith('/sessions') || p.startsWith('/schoolsessions')) return 'school_sessions';
  if (p === '/driving') return 'driving';
  if (p.startsWith('/retail')) return 'retail';
  if (p === '/notifications') return 'notifications';
  if (p === '/search') return 'search';
  if (p === '/wheel') return 'wheel';
  if (p === '/hub' || p === '/profile' || p.startsWith('/profile/')) return 'profile';
  return 'other';
}

export function resolvePlaySource() {
  let explicit = null;
  try {
    const at = Number(window.__feelz_play_source_at) || 0;
    if (window.__feelz_play_source && Date.now() - at < SOURCE_TTL_MS) {
      explicit = window.__feelz_play_source;
    }
  } catch { /* ignore */ }
  return explicit || sourceFromPath(window.location?.pathname);
}

// Somebody who arrived from Plugin Gallery or the FiveM server is a different
// person from somebody already on the platform, and until now there was no way
// to tell them apart: the app read no referrer and no parameters at all.
//
// This goes in its OWN column rather than being folded into event_source.
// Where somebody came from and what screen they pressed play on are two
// different questions, and mixing two dimensions into one text column is what
// made event_source hard to read in the first place. Keeping them apart means
// "album plays from the FiveM server" is a question with an answer.
//
// sessionStorage rather than localStorage on purpose. It should describe this
// visit, not brand somebody an external visitor for the rest of the year.
const EXTERNAL_KEY = 'fm_entry_from';

export function readExternalEntry() {
  try {
    const q = new URLSearchParams(window.location.search);
    const raw = q.get('from') || q.get('utm_source');
    if (raw) {
      const clean = raw.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24);
      if (clean) { sessionStorage.setItem(EXTERNAL_KEY, clean); return clean; }
    }
    return sessionStorage.getItem(EXTERNAL_KEY) || null;
  } catch {
    return null;
  }
}

// Preload a track's cover art into the browser cache so VinylRecord/Cassette show instantly
// ── Telling an interruption apart from a pause ───────────────────────────────
//
// A call, a navigation prompt, another app taking the audio: every one of them
// reaches this app as an ordinary pause event, carrying nothing that says it
// was not a person pressing pause. The browser does not tell you. So the
// difference has to be tracked on our side, by marking every pause we asked
// for, and treating the rest as the phone taking the audio away.
//
// The rule that matters most is the one about being unsure. Resuming over a
// deliberate pause is far worse than failing to resume after an interruption:
// somebody who stopped the music at a drive-through window and had it start
// talking again by itself would rightly call that broken, while somebody whose
// music did not come back after a call just presses play. So anything this
// cannot positively identify as an interruption is left alone.
//
// Exported and pure so the decision can be tested on its own, away from an
// audio element and a browser. The order of the checks is the whole logic:
// deliberate wins over everything, because our own crossfade pauses the
// element mid-fade and a track change pauses it before swapping the source,
// and both of those would otherwise look exactly like a phone call.
export function classifyPause({ deliberate, crossfading, ended, hasSrc }) {
  if (deliberate)  return 'deliberate';   // we asked for this one
  if (crossfading) return 'crossfade';    // our own fade between tracks
  if (ended)       return 'ended';        // the track simply finished
  if (!hasSrc)     return 'idle';         // nothing was loaded to interrupt
  return 'interrupted';                   // the phone took the audio
}

// Which track plays next when nothing is shuffled.
//
// Deliberately a mirror of the non-shuffle branch inside playNextFromRef
// rather than a shared call, because that function is in the middle of a
// crossfade and is not somewhere to introduce a refactor. If the two ever
// disagree the cost is small and silent: the preloader fetches a track that
// does not end up playing, which wastes a little data and helps nobody. Worth
// knowing, not worth risking the crossfade over.
//
// Returns null when there is no next track, which is the end of a queue that
// is not repeating.
export function nextSequentialIndex({ length, index, repeat }) {
  if (!length) return null;
  if (repeat === 'one') return null;     // the same track again, already loaded
  const next = index + 1;
  if (next < length) return next;
  return repeat === 'all' ? 0 : null;
}

// ── Claiming the car's speakers ─────────────────────────────────────────────
//
// Two things can be playing audio on a phone at once, and which one you hear
// is not something the web normally gets a say in. Open a YouTube tab while a
// track is playing and YouTube wins, because it started last. In a car that is
// the difference between music and somebody's video essay.
//
// The Audio Session API is the say. Setting the type to 'playback' declares
// this as primary media audio, which is an exclusive claim: the system pauses
// other playback audio and leaves non-playback sounds, like a navigation
// prompt or a notification, alone. Which is the right split. Nobody wants the
// turn instruction suppressed.
//
// WHERE IT WORKS, HONESTLY
//
// Safari on iOS, and it is still marked experimental. Chrome on Android does
// not implement it and decides focus for itself, so there this is a no-op and
// the resume logic is what carries. Everything here is wrapped because an
// experimental API on a moving target should never be able to stop the music.
//
// CLAIMED ONLY WHILE PLAYING
//
// This is an exclusive claim over every other app's audio, so it is taken when
// a track starts and given back on a deliberate pause. Holding it while
// nothing plays would mean an app sitting open in a background tab quietly
// stopping something else from making a sound, which is not ours to do.
function claimAudioSession() {
  try {
    if (typeof navigator !== 'undefined' && navigator.audioSession) {
      navigator.audioSession.type = 'playback';
    }
  } catch {
    // Experimental, and the accepted values have changed once already. A
    // browser that rejects the value keeps whatever it had.
  }
}

function releaseAudioSession() {
  try {
    if (typeof navigator !== 'undefined' && navigator.audioSession) {
      navigator.audioSession.type = 'auto';
    }
  } catch {}
}

function preloadCover(track) {
  if (!track?.cover_artwork_url) return;
  const img = new window.Image();
  img.crossOrigin = 'anonymous';
  img.src = track.cover_artwork_url;
}

// ── Offline playback ─────────────────────────────────────────────────────────
//
// Every place that assigns audio.src now goes through playbackSrc, which
// returns the copy stored on this device when there is one and the streaming
// URL otherwise. It is deliberately synchronous, see the comment on it in
// utils/offlineStore.js, so an online listener never waits on a storage read
// to discover they have nothing saved.
//
// resolveLocalLater is the safety net for the one case playbackSrc cannot
// answer synchronously: a saved track on a page with no service worker
// controlling it, where the local copy has to be handed over as a blob URL.
// It matters when that coincides with actually being offline, which is exactly
// when getting it wrong is least forgivable.
function resolveLocalLater(audio, track, { onSwap } = {}) {
  if (!track?.id) return;
  if (!isSavedOfflineSync(track.id)) return;
  // The saved copy is of a file this track no longer points at, so swapping it
  // in would undo what playbackSrc just decided and put the old master back.
  if (offlineCopyIsStale(track)) return;
  if (audio.src && audio.src.includes('/offline-audio/')) return;  // already local

  offlineSrcFor(track.id).then(localSrc => {
    if (!localSrc) return;
    // The listener may have moved on while we were reading. Only swap if this
    // element is still pointed at the track we resolved for.
    if (audio.dataset.feelzTrackId !== String(track.id)) return;
    if (audio.src === localSrc) return;
    const at = audio.currentTime;
    audio.src = localSrc;
    audio.load();
    if (at > 0) {
      const restore = () => {
        try { audio.currentTime = at; } catch {}
        audio.removeEventListener('loadedmetadata', restore);
      };
      audio.addEventListener('loadedmetadata', restore);
    }
    onSwap?.(localSrc);
  }).catch(() => { /* streaming URL stays; never break playback over this */ });
}

const PlayerContext = createContext({});

function PlayerProviderInner({ children, value, isPlaying, togglePlay, playNext, playPrev, currentTrack, seek, currentTime, duration }) {
  useMediaSession({ currentTrack, isPlaying, togglePlay, playNext, playPrev, seek, currentTime, duration });
  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function PlayerProvider({ children }) {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [duration, setDuration]         = useState(0);
  const [currentTime, setCurrentTime]   = useState(0);
  const [volume, setVolume]             = useState(1);
  const [queue, setQueue]               = useState([]);
  const [queueIndex, setQueueIndex]     = useState(-1);
  const [shuffle, setShuffle]           = useState(false);
  const [repeat, setRepeat]             = useState('none');
  const [isMinimized, setIsMinimized]   = useState(false);
  // Why a blocked tap did nothing. Rendered by this provider itself (see the
  // bottom of the file) rather than wired into a page, so every one of the 39
  // playTrack call sites gets the explanation for free.
  const [playbackNotice, setPlaybackNotice] = useState(null);
  // Which tab the desktop docked player panel opens on when triggered,
  // shared so DesktopPlayer's Queue button and FullPlayer's own tabs
  // drive the same single panel instead of two competing ones.
  const [desktopPanelView, setDesktopPanelView] = useState('player');

  const audioRef        = useRef(new Audio());
  const audioRefB       = useRef(new Audio());  // second element for crossfade
  const crossfadingRef  = useRef(false);
  const CROSSFADE_SECS  = 1.5; // seconds of overlap, short enough to not echo
  const streamLoggedRef = useRef(false);

  // Captured once when a track starts, then used by every write for that play.
  // Reading the source again at write time is what let it drift: the listening
  // event is written when the play ENDS, which can be several navigations
  // later, so the person may be on a different screen entirely by then.
  const playSourceRef = useRef('unknown');
  const entryFromRef = useRef(null);
  // Flipped to false the first time the database says it has no entry_from
  // column, so a build that lands before migration 203 keeps recording plays.
  const entryFromSupportedRef = useRef(true);

  // A third element that never plays. Its whole job is to have already pulled
  // the next track's audio down the wire so the real element does not have to.
  // See the preload effect further down.
  const preloadRef      = useRef(new Audio());
  const preloadedForRef = useRef(null);

  // Interruption handling. deliberatePauseRef covers every pause this app
  // asked for, whether the person pressed it or the code did during a track
  // change. Anything else that stops the audio was the phone, not us.
  const deliberatePauseRef = useRef(false);
  const interruptedRef     = useRef(false);
  const resumeTriesRef     = useRef(0);

  // ── THE LISTEN-THROUGH BUFFER ─────────────────────────────────────────────
  //
  // THE PROBLEM. A streaming audio element only ever holds the few seconds it
  // has buffered ahead. Lose signal in a lift, a tunnel, a bad patch of the
  // highway, and it runs out of bytes and stops, even though the person was
  // most of the way through the song. Nothing in this app stops it and the
  // signed URL is good for two hours, so there is nothing to fix in the
  // plumbing. The element simply does not have the rest of the file.
  //
  // THE ONLY FIX IS TO HAVE THE BYTES. So after twenty seconds of actual
  // playback, the whole file is fetched once in the background and kept as a
  // blob. If the element then runs dry while the network is gone, playback
  // swaps to the blob at the same position and the song finishes.
  //
  // WHY TWENTY SECONDS. Steve's call, and the right one. Fetching from the
  // first second would cover a drop at 0:05, and would also pull a full file
  // for every person who skips after a few seconds, which is most of the
  // skipping that happens. Twenty seconds is somebody who has decided to
  // listen. The trade is explicit: a drop inside the first twenty seconds
  // still cuts out.
  //
  // IN MEMORY ONLY, AND FOR ONE TRACK. Nothing is written to IndexedDB, which
  // keeps this entirely clear of two things it has no business touching: the
  // storage quota the offline downloads manage, and the entitlement rules that
  // decide who may keep a paid track. Streaming a song and keeping its bytes
  // for the length of that song are the same permission. Keeping them after it
  // is not, and this does not.
  //
  // COST. One extra pull of the file for a listener who gets past twenty
  // seconds, on top of what the element streams. Not free, and worth it for a
  // song that finishes.
  const LISTEN_BUFFER_AFTER_S    = 20;
  const LISTEN_BUFFER_MAX_BYTES  = 40 * 1024 * 1024;
  const STALL_GRACE_MS           = 2500;
  // { trackId, state: idle | fetching | ready | failed, url, controller }
  const listenBufRef   = useRef({ trackId: null, state: 'idle', url: null, controller: null });
  const stallTimerRef  = useRef(null);

  // WHAT HAPPENS WHEN THERE IS NO BUFFER TO SWAP TO.
  //
  // swapToBuffer below covers the case the listen buffer was built for: the
  // network dies after twenty seconds and the bytes are already in memory. In
  // every other case it returns early and did nothing at all — no retry, no
  // fallback, no signal. That is a drop inside the first twenty seconds, a
  // background fetch that failed or is still running, or a track whose buffer
  // belongs to the previous song. The element is left with a dead source, and
  // a media element in that state never recovers on its own: its network state
  // is terminal, so it sits there silent even once the signal is back.
  //
  // From the outside that is a song that stops, or one that glitches as though
  // it never fully loaded. These two carry the fallback: where the listener
  // was, so the stream can be reopened at that point, and whether we are
  // currently stalled, so the bar can say "buffering" instead of claiming the
  // track is playing while no sound is coming out.
  const streamResumeAtRef = useRef(0);
  const [isBuffering, setIsBuffering] = useState(false);

  // Driving mode is an explicit statement that this phone is doing one job
  // right now. It buys a longer fight for the speakers, and nothing else.
  // Opening the screen is the consent; there is no version of this that turns
  // itself on because the app guessed somebody was in a car.
  const drivingRef = useRef(false);

  // ── Listening events ────────────────────────────────────────────────────
  // Taste signal capture for the For You model. Deliberately separate from
  // log_stream: that writes one row at 30 seconds with completed hardcoded
  // true and drives artist payouts, so it carries no completion signal and
  // must not be changed for scoring reasons.
  //
  // This lives in PlayerContext rather than in any single page so every
  // surface that plays through playTrack is covered by one write point.
  //
  // currentTrackRef mirrors currentTrack because the flush runs inside
  // callbacks and event listeners where the state value would be stale.
  const currentTrackRef  = useRef(null);
  const lastFlushedRef   = useRef(null);
  const geoRef           = useRef(null);

  // Approximate location for the play, from Netlify's edge geo header.
  // Resolved once and cached in sessionStorage: it does not change mid
  // listen, and a busy listener would otherwise hit this on every track
  // change. Any failure resolves to nulls rather than throwing, because a
  // play with unknown location must still be recorded.
  const resolveGeo = useCallback(async () => {
    if (geoRef.current) return geoRef.current;
    try {
      const cached = sessionStorage.getItem('feelz_geo');
      if (cached) {
        geoRef.current = JSON.parse(cached);
        return geoRef.current;
      }
    } catch {}
    try {
      const res = await fetch('/.netlify/functions/geo');
      const geo = await res.json();
      geoRef.current = geo;
      try { sessionStorage.setItem('feelz_geo', JSON.stringify(geo)); } catch {}
      return geo;
    } catch {
      geoRef.current = { country: null, country_name: null, city: null, region: null };
      return geoRef.current;
    }
  }, []);

  // The id of the stream row logged for the current play, so the real
  // duration and completion can be written back to it when the play ends.
  // Cleared on every track change so one track's outcome can never be
  // written onto another's row.
  const currentStreamIdRef = useRef(null);

  const flushListeningEvent = useCallback(async (endReason) => {
    const track = currentTrackRef.current;
    const audio = audioRef.current;
    if (!track || !audio) return;

    const listened = Math.floor(audio.currentTime || 0);
    // Under three seconds is a mis-tap or a queue skip-through, not a
    // listening decision. Recording it would be noise.
    if (listened < 3) return;

    // Guard against double flush when two paths fire for the same play,
    // for example ended followed by a track change.
    const flushKey = `${track.id}:${Math.floor(Date.now() / 1000)}`;
    if (lastFlushedRef.current === flushKey) return;
    lastFlushedRef.current = flushKey;

    // Take the stream id NOW, while we are still synchronous.
    //
    // This function is called without await, and everything below the first
    // `await` runs after control has returned to the caller. The track-change
    // path clears currentStreamIdRef on the line immediately after calling
    // us, so by the time the old code read the ref (after two awaits) it was
    // always null, and finalise_stream never ran on a track change.
    //
    // That is the most common way a play ends: a skip. So the stream row kept
    // the placeholder `completed = false` and the 30-second duration that
    // migration 79 exists to replace, for every listen except a track played
    // to the end or interrupted by a page hide.
    //
    // Reading and clearing it here closes the race and makes the caller's
    // defensive clear redundant rather than harmful.
    const streamId = currentStreamIdRef.current;
    currentStreamIdRef.current = null;

    const trackSeconds = Math.floor(audio.duration || track.duration || 0) || null;
    const pct = trackSeconds ? Math.min(100, Math.round((listened / trackSeconds) * 1000) / 10) : null;
    const geo = await resolveGeo();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;

      const row = {
        user_id:          userId,
        track_id:         track.id,
        artist_id:        track.artist_id || null,
        genre:            track.genre || null,
        mood:             track.mood || null,
        bpm:              track.bpm || null,
        is_beat:          track.is_beat === true,
        listened_seconds: listened,
        track_seconds:    trackSeconds,
        completion_pct:   pct,
        end_reason:       endReason,
        event_source:     playSourceRef.current || 'unknown',
        country:          geo?.country || null,
        country_name:     geo?.country_name || null,
        city:             geo?.city || null,
        region:           geo?.region || null,
      };
      if (entryFromSupportedRef.current) row.entry_from = entryFromRef.current;

      // entry_from is added by migration 203. If this build reaches people
      // before that migration is run, PostgREST rejects the whole insert
      // because of the unknown column, and the platform stops recording ANY
      // listening events, which is the signal the entire feed is built on.
      //
      // So the column is dropped and the insert retried once. Losing where a
      // visit came from is a footnote; losing every play for as long as it
      // takes somebody to notice is not. The flag means the retry happens
      // once per session rather than on every play.
      const { error: insertError } = await supabase.from('listening_events').insert(row);
      if (insertError && entryFromSupportedRef.current) {
        const missingColumn =
          insertError.code === 'PGRST204' ||
          /entry_from/.test(insertError.message || '');
        if (missingColumn) {
          entryFromSupportedRef.current = false;
          delete row.entry_from;
          await supabase.from('listening_events').insert(row);
        }
      }

      // The stream row was written at the 30 second mark with completed
      // false, because at that point nothing had been completed. Now the
      // play has actually ended, so tell it what happened. Without this
      // streams.completed and duration_played stay constants, which is
      // what made the dashboard report 100% completion and 0:30 average
      // for every artist.
      //
      // 'ended' is the only end reason that means finished. A page hide or
      // a track change at 95% is still not a completed listen, and
      // completion_pct on listening_events already records how far they got.
      // streamId was captured at the top, before any await. Re-reading the
      // ref here is what the race defeated.
      if (streamId) {
        const { error: finaliseError } = await supabase.rpc('finalise_stream', {
          p_stream_id: streamId,
          p_duration_played: listened,
          p_completed: endReason === 'ended',
        });
        if (finaliseError) console.warn('[player] finalise_stream failed:', finaliseError.message);
      }
    } catch {
      // Never let signal capture break playback. A lost event is a lost
      // data point, a thrown error here would be a broken player.
    }
  }, [resolveGeo]);
  const queueRef        = useRef([]);
  const queueIndexRef   = useRef(-1);
  const shuffleRef      = useRef(false);
  const repeatRef       = useRef('none');
  const volumeRef       = useRef(1);

  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { queueIndexRef.current = queueIndex; }, [queueIndex]);
  useEffect(() => { shuffleRef.current = shuffle; }, [shuffle]);
  useEffect(() => { repeatRef.current = repeat; }, [repeat]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // Sign ahead of the listener.
  //
  // A signed URL has to be fetched, and fetching it at the moment of a tap
  // would put a round trip in front of every skip. So whenever the queue or
  // the position in it changes, the current track and the next few are signed
  // in one batched request. By the time anyone presses next, playbackSrc()
  // already has the URL and the swap never happens.
  //
  // No-op unless REACT_APP_PRIVATE_AUDIO is set.
  useEffect(() => {
    if (!queue?.length) return;
    const start = Math.max(0, queueIndex);
    const ids = queue.slice(start, start + 4).map(t => t?.id).filter(Boolean);
    if (ids.length) warmStreamUrls(ids);
  }, [queue, queueIndex]);

  // ── Pull the next track down before it is needed ────────────────────────
  //
  // The URL was already being signed ahead by the effect above, which removed
  // the round trip. What it did not do is fetch any audio, so at the moment of
  // a track change the element still has to open a connection and buffer
  // enough to fire canplay before a note comes out.
  //
  // On headphones that lands inside the crossfade and nobody notices. In a car
  // it is the thing you actually hear: the last track fades away and there is
  // a second or two of road noise before the next one arrives. Road noise is
  // what a stall sounds like at 120km/h.
  //
  // So once the current track passes halfway, a third audio element is pointed
  // at the next one. It never plays and is muted so it cannot. Setting src and
  // calling load() is what asks the browser to start buffering, and because
  // both elements resolve to the same URL, the real element's request lands on
  // what this one already fetched.
  //
  // HONEST ABOUT WHERE THIS WORKS
  //
  // preload is a hint, not an instruction. Chrome and Edge on Android honour
  // it. Safari on iOS frequently ignores it on a cellular connection, which is
  // deliberate on Apple's part and cannot be forced. So this helps most
  // exactly where most Android drivers are, and does no harm where it is
  // ignored.
  //
  // Halfway rather than earlier, because someone skipping through a playlist
  // never reaches it and never spends the data.
  const preloadNext = useCallback(() => {
    // Shuffle picks at random at the moment of the change, so there is no next
    // track to preload. Guessing one would just be data spent on a track that
    // is very unlikely to play.
    if (shuffleRef.current) return;

    const q   = queueRef.current;
    const nextIdx = nextSequentialIndex({
      length: q.length,
      index:  queueIndexRef.current,
      repeat: repeatRef.current,
    });
    if (nextIdx == null) return;

    const next = q[nextIdx];
    if (!next?.file_url) return;

    // Already on the device. The service worker serves it locally and there is
    // nothing to fetch.
    if (isSavedOfflineSync(next.id)) return;

    // Somebody on a metered connection who has asked their phone to save data
    // has not agreed to us fetching a song they have not chosen to hear yet.
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn?.saveData) return;
    if (conn?.effectiveType && /^(slow-)?2g$/.test(conn.effectiveType)) return;

    const src = playbackSrc(next);
    if (!src) return;

    const el = preloadRef.current;
    if (el.src === src) return;
    try {
      el.muted   = true;
      el.preload = 'auto';
      el.src     = src;
      el.load();
    } catch {
      // A failed preload is not a failed anything. The track will load the
      // ordinary way a moment later.
    }
  }, []);

  useEffect(() => {
    if (!duration || !currentTrack?.id) return;
    if (currentTime < duration / 2) return;
    if (preloadedForRef.current === currentTrack.id) return;
    preloadedForRef.current = currentTrack.id;
    preloadNext();
  }, [currentTime, duration, currentTrack?.id, preloadNext]);

  const fetchingSuggestionsRef = useRef(false);

  // When we reach the last 2 tracks in the queue, silently fetch similar tracks and append
  const extendQueueWithSuggestions = useCallback(async (currentTrack) => {
    if (fetchingSuggestionsRef.current || !currentTrack) return;
    fetchingSuggestionsRef.current = true;
    try {
      const filters = [];
      if (currentTrack.genre) filters.push(`genre.eq.${currentTrack.genre}`);
      if (currentTrack.mood)  filters.push(`mood.eq.${currentTrack.mood}`);

      const existingIds = queueRef.current.map(t => t.id).filter(Boolean);

      let query = supabase
        .from('tracks')
        .select('*, artists!tracks_artist_id_fkey(artist_name, slug, profile_image_url)')
        .eq('is_published', true)
        .not('id', 'in', `(${existingIds.join(',')})`)
        .order('engagement_score', { ascending: false })
        .limit(10);

      if (filters.length > 0) query = query.or(filters.join(','));

      const { data } = await query;
      if (!data?.length) return;

      const normalised = data.map(t => ({
        ...t,
        artist_name: t.artists?.artist_name || t.artist_name || 'Unknown Artist',
        artist_slug: t.artists?.slug || null,
      }));

      setQueue(prev => [...prev, ...normalised]);
      queueRef.current = [...queueRef.current, ...normalised];
    } catch (err) {
      console.error('Queue suggestion error:', err);
    } finally {
      fetchingSuggestionsRef.current = false;
    }
  }, []);

  // THE NAME UNDER THE TITLE, WHEREVER THE TRACK CAME FROM.
  //
  // The player renders `currentTrack.artist_name || 'Unknown Artist'`, and a
  // dozen queries around the app select a track without joining artists:
  // stories, posts, the collection page, sessions, the leaderboard. Played
  // from any of those, a real artist with a real name showed as Unknown
  // Artist, including on artwork with the name printed on it.
  //
  // Fixing it query by query means finding all of them and then remembering
  // forever. This is the gate every playback path already goes through, so
  // the name is filled in here instead: once, only when it is missing, and
  // only for the track on screen. A failed lookup leaves what was there.
  //
  // Declared above playNextFromRef on purpose, because that calls it.
  const fillArtistName = useCallback(async (track) => {
    if (!track?.id || track.artist_name) return;
    try {
      const { data } = await supabase
        .from('tracks')
        .select('artists!tracks_artist_id_fkey(artist_name)')
        .eq('id', track.id)
        .maybeSingle();
      const name = data?.artists?.artist_name;
      if (!name) return;
      setCurrentTrack(prev => (prev && prev.id === track.id ? { ...prev, artist_name: name } : prev));
      const patch = (list) => (list || []).map(t => (t.id === track.id ? { ...t, artist_name: name } : t));
      setQueue(prev => patch(prev));
      queueRef.current = patch(queueRef.current);
    } catch {
      // The title and the artwork are already on screen. A missing name is
      // not worth an error state.
    }
  }, []);

  const playNextFromRef = useCallback(() => {
    const q   = queueRef.current;
    const idx = queueIndexRef.current;
    const isShuffled = shuffleRef.current;
    const rep = repeatRef.current;
    if (q.length === 0) return;
    let nextIndex;
    if (isShuffled) {
      // Exclude current track from shuffle pick
      if (q.length === 1) {
        nextIndex = 0;
      } else {
        do {
          nextIndex = Math.floor(Math.random() * q.length);
        } while (nextIndex === idx);
      }
    } else {
      nextIndex = idx + 1;
      if (nextIndex >= q.length) {
        if (rep === 'all') { nextIndex = 0; } else { return; }
      }
    }
    const nextTrack = q[nextIndex];
    if (nextTrack?.file_url) {
      // Before the crossfade overwrites the playhead, capture how far the
      // outgoing track actually got.
      flushListeningEvent('track_change');
      // flushListeningEvent now takes and clears this synchronously, so this
      // line is belt and braces for the case where the flush bailed before
      // reaching it (under three seconds, or the duplicate guard) and the id
      // would otherwise survive into the next track.
      currentStreamIdRef.current = null;
      streamLoggedRef.current = false;

      // ── Crossfade: use a temporary second element to fade out the current
      // track, then switch audioRef (the primary, event-listened element) to
      // the new track once the overlap completes.
      // We never swap refs, audioRef stays as the primary element throughout
      // so all event listeners (timeupdate, ended, play, pause) remain valid.
      const primaryAudio = audioRef.current;
      const targetVol    = volumeRef.current;
      crossfadingRef.current = true;

      // Clone current position into fadeOut BEFORE pausing primary
      const fadeOutAudio    = audioRefB.current;
      const currentSrc      = primaryAudio.src;
      const currentPosition = primaryAudio.currentTime;

      // Pause primary immediately, prevents echo from two elements on same src
      primaryAudio.pause();

      // Set fadeOut to the OLD track at the exact playback position
      fadeOutAudio.src    = currentSrc;
      fadeOutAudio.volume = targetVol;
      try {
        fadeOutAudio.currentTime = currentPosition;
        fadeOutAudio.play().catch(() => {});
      } catch {}

      // Now switch primary to the NEW track (silent, fades in)
      primaryAudio.dataset.feelzTrackId = String(nextTrack.id);
      primaryAudio.volume = 0;
      // The queue warmer has normally signed this track already, in which case
      // this assigns and loads before yielding and the crossfade is unchanged.
      // On the rare cold cache it waits rather than assigning a null src,
      // which is what playbackSrc now returns instead of a dead public URL.
      assignPlaybackSrc(primaryAudio, nextTrack);
      // No resolveStreamLater here, for the same reason as resolveLocalLater
      // below: swapping src mid-crossfade restarts the incoming track under
      // the fade. The queue warmer above has normally signed this track
      // already, in which case playbackSrc returned the signed URL and there
      // is nothing to swap.
      // Not resolveLocalLater here on purpose: swapping src mid-crossfade
      // would restart the incoming track under the fade. A saved track is
      // served through the worker synchronously anyway; the blob fallback
      // just streams for this one transition.
      const startFadeIn = () => {
        primaryAudio.play().catch(() => {});
        primaryAudio.removeEventListener('canplay', startFadeIn);
      };
      primaryAudio.addEventListener('canplay', startFadeIn);

      setCurrentTrack(nextTrack);
      fillArtistName(nextTrack);
      preloadCover(nextTrack);
      if (q[nextIndex + 1]) preloadCover(q[nextIndex + 1]);
      setQueueIndex(nextIndex);
      setCurrentTime(0);

      const steps    = 30;
      const interval = (CROSSFADE_SECS * 1000) / steps;
      let   step     = 0;

      const fade = setInterval(() => {
        step++;
        const progress        = step / steps;
        fadeOutAudio.volume   = Math.max(0, targetVol * (1 - progress));
        primaryAudio.volume   = Math.min(targetVol, targetVol * progress);
        if (step >= steps) {
          clearInterval(fade);
          fadeOutAudio.pause();
          fadeOutAudio.src    = '';
          fadeOutAudio.volume = targetVol;
          primaryAudio.volume = targetVol;
          crossfadingRef.current = false;
        }
      }, interval);

      // When 2 or fewer tracks remain, extend queue silently
      if (!shuffleRef.current && rep !== 'all' && q.length - nextIndex <= 2) {
        extendQueueWithSuggestions(nextTrack);
      }
    }
  }, [extendQueueWithSuggestions, flushListeningEvent]);

  useEffect(() => { currentTrackRef.current = currentTrack; }, [currentTrack]);

  // playNextFromRef is rebuilt whenever extendQueueWithSuggestions or
  // flushListeningEvent change. The stall effect below registers its listeners
  // once and must not re-register on every one of those rebuilds, because
  // tearing down mid-stall would drop the timer that is waiting to recover the
  // track. So it calls through this, which always points at the current one.
  const playNextFnRef = useRef(playNextFromRef);
  useEffect(() => { playNextFnRef.current = playNextFromRef; }, [playNextFromRef]);

  // Catches the listener closing the tab or backgrounding the app, which
  // is otherwise the most common way a play goes unrecorded.
  useEffect(() => {
    const onHide = () => { flushListeningEvent('page_hide'); };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') onHide();
    });
    return () => {
      window.removeEventListener('pagehide', onHide);
    };
  }, [flushListeningEvent]);

  useEffect(() => {
    const audio = audioRef.current;
    const onTimeUpdate      = () => setCurrentTime(audio.currentTime);
    const onDurationChange  = () => setDuration(audio.duration || 0);
    const onEnded           = () => {
      flushListeningEvent('ended');
      if (repeatRef.current === 'one') {
        audio.currentTime = 0;
        audio.play().catch(console.error);
      } else {
        playNextFromRef();
      }
    };
    const onPlay  = () => {
      setIsPlaying(true);
      // Take the speakers at the moment we actually start making sound, not
      // before. See claimAudioSession.
      claimAudioSession();
      // Playing again clears the slate. Whatever stopped us is over, and the
      // retry budget is back for the next interruption.
      interruptedRef.current = false;
      resumeTriesRef.current = 0;
    };

    const onPause = () => {
      setIsPlaying(false);

      const verdict = classifyPause({
        deliberate:  deliberatePauseRef.current,
        crossfading: crossfadingRef.current,
        ended:       audio.ended,
        hasSrc:      !!audio.src,
      });

      // The flag is one-shot: it described the pause that just happened and
      // must not describe the next one.
      if (verdict === 'deliberate') { deliberatePauseRef.current = false; return; }
      if (verdict !== 'interrupted') return;

      interruptedRef.current = true;
      resumeTriesRef.current = 0;
    };
    audio.addEventListener('timeupdate',     onTimeUpdate);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('ended',          onEnded);
    audio.addEventListener('play',           onPlay);
    audio.addEventListener('pause',          onPause);
    return () => {
      audio.removeEventListener('timeupdate',     onTimeUpdate);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('ended',          onEnded);
      audio.removeEventListener('play',           onPlay);
      audio.removeEventListener('pause',          onPause);
      audio.pause();
    };
  }, [playNextFromRef, flushListeningEvent]);

  // ── Picking the music back up after something took the audio ────────────
  //
  // A call, a navigation prompt, a voice assistant. The phone pauses the audio
  // and hands it back when it is done, and whether playback resumes is up to
  // the browser. Chrome on Android usually resumes. Safari on iOS usually does
  // not, and what the driver gets is silence for the rest of the journey until
  // they pick the phone up, which is the moment you least want them to.
  //
  // There is no interruption-ended event on the web, so there is nothing to
  // wait for. What there is: the tab becomes visible again when the call ends
  // and the person comes back to the app, and a short retry covers the case
  // where the audio was handed back while the app stayed in the background.
  //
  // THE LIMIT, DELIBERATELY
  //
  // Three attempts over about six seconds, then it stops. This is not a loop
  // that keeps trying to start the music. If something is holding the audio
  // for longer than that, it wants the audio more than we do, and a car that
  // starts playing music by itself several minutes later is worse than one
  // that stays quiet. The play button is right there.
  //
  // Driving mode raises it to eight, about sixteen seconds. Somebody who
  // opened that screen has said what this phone is for, and a call that lasts
  // a few rings should not end the music for the rest of the journey. It is
  // still a limit, not a loop.
  const RESUME_TRIES         = 3;
  const RESUME_TRIES_DRIVING = 8;
  const RESUME_SPACING_MS    = 2000;

  useEffect(() => {
    const audio = audioRef.current;

    const tryResume = () => {
      if (!interruptedRef.current) return;
      if (!audio.src || audio.ended || audio.paused === false) return;
      const budget = drivingRef.current ? RESUME_TRIES_DRIVING : RESUME_TRIES;
      if (resumeTriesRef.current >= budget) { interruptedRef.current = false; return; }
      resumeTriesRef.current += 1;
      // Re-claim on every attempt, not just the first. Whatever took the audio
      // claimed it for itself, so asking to play again without asking for the
      // speakers back is asking for the half that does not matter.
      claimAudioSession();
      audio.play().catch(() => {
        // Blocked, usually because the browser wants a gesture first, or the
        // audio is still held by whatever took it. Not an error worth showing
        // anyone: either a later attempt lands or they press play.
      });
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') tryResume();
    };

    document.addEventListener('visibilitychange', onVisible);

    // The background case. Cheap enough to leave running: it does nothing at
    // all unless an interruption was recorded, and it gives up after three.
    const timer = setInterval(() => {
      if (interruptedRef.current) tryResume();
    }, RESUME_SPACING_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, []);

  // ── Listen-through buffer: release ────────────────────────────────────────
  //
  // Declared BEFORE the effect that starts a fetch, so on a track change this
  // one runs first and the new track never inherits the old track's blob.
  //
  // The revoke is delayed rather than immediate, and that is not sloppiness.
  // When the next track starts, the crossfade copies this element's src onto
  // the second element to fade the outgoing track out over CROSSFADE_SECS. If
  // the outgoing track was playing from the blob, revoking the URL in the same
  // tick pulls the source out from under a fade that is still running. Fifteen
  // seconds is far longer than any fade and still frees the memory.
  const releaseListenBuffer = useCallback(() => {
    const b = listenBufRef.current;
    if (b.controller) { try { b.controller.abort(); } catch {} }
    if (b.url) {
      const dying = b.url;
      setTimeout(() => { try { URL.revokeObjectURL(dying); } catch {} }, 15000);
    }
    listenBufRef.current = { trackId: null, state: 'idle', url: null, controller: null };
  }, []);

  useEffect(() => {
    releaseListenBuffer();
    return () => releaseListenBuffer();
  }, [currentTrack?.id, releaseListenBuffer]);

  // ── Listen-through buffer: fetch ──────────────────────────────────────────
  //
  // Rides the existing currentTime tick rather than adding a timer. Starts at
  // most once per track: after the first pass the state is no longer idle and
  // every later tick returns immediately.
  useEffect(() => {
    const track = currentTrack;
    const audio = audioRef.current;
    if (!track?.id || !audio) return;
    if (currentTime < LISTEN_BUFFER_AFTER_S) return;

    const b = listenBufRef.current;
    if (b.trackId === track.id && b.state !== 'idle') return;

    const src = audio.currentSrc || audio.src;
    if (!src) return;
    // Already local. A saved download or a blob we are already playing needs
    // no second copy, and /offline-audio/ is served by the service worker out
    // of IndexedDB, which the network cannot take away.
    if (src.startsWith('blob:') || src.includes('/offline-audio/')) return;

    const controller = new AbortController();
    listenBufRef.current = { trackId: track.id, state: 'fetching', url: null, controller };

    (async () => {
      try {
        const res = await fetch(src, { signal: controller.signal, credentials: 'omit' });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const declared = Number(res.headers.get('content-length') || 0);
        if (declared && declared > LISTEN_BUFFER_MAX_BYTES) throw new Error('larger than the cap');
        const blob = await res.blob();
        if (blob.size > LISTEN_BUFFER_MAX_BYTES) throw new Error('larger than the cap');

        // Two checks, not one. The id catches a track change. The src catches
        // the subtler case of the same track whose URL was re-signed while the
        // fetch was in flight, where the bytes may be fine but we can no
        // longer prove they belong to what is playing. Conservative on
        // purpose: the cost of discarding is one unused fetch, the cost of
        // being wrong is the wrong song.
        const live = audioRef.current;
        if (listenBufRef.current.trackId !== track.id) return;
        if ((live?.currentSrc || live?.src) !== src) {
          listenBufRef.current = { ...listenBufRef.current, state: 'failed', controller: null };
          return;
        }

        listenBufRef.current = {
          trackId: track.id,
          state: 'ready',
          url: URL.createObjectURL(blob),
          controller: null,
        };
      } catch {
        // A failed buffer is not a failed anything. Playback carries on
        // exactly as it does today; there is simply nothing to fall back to.
        if (listenBufRef.current.trackId === track.id) {
          listenBufRef.current = { ...listenBufRef.current, state: 'failed', controller: null };
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, currentTrack?.id]);

  // ── Listen-through buffer: recover ────────────────────────────────────────
  //
  // WHAT COUNTS AS RUNNING DRY. An `error` is unambiguous and swaps at once.
  // `waiting` and `stalled` are not: they fire on ordinary seeks and during
  // normal buffering, so swapping on them directly would interrupt healthy
  // playback. They start a timer instead, and any sign of progress cancels it.
  // Only a stall that outlasts STALL_GRACE_MS is treated as the network
  // having gone away.
  //
  // THE PAUSE THAT THE SWAP CAUSES. Assigning src pauses the element, and
  // onPause would read that as the phone taking the audio, set interruptedRef
  // and have tryResume racing this swap to press play. deliberatePauseRef is
  // exactly the flag for "we asked for this one", so the swap sets it.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const clearStallTimer = () => {
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
      setIsBuffering(false);
    };

    // Returns whether it actually swapped, so the caller can fall back. It
    // used to return undefined in every branch and the three early exits were
    // indistinguishable from success.
    const swapToBuffer = () => {
      clearStallTimer();
      const b = listenBufRef.current;
      if (b.state !== 'ready' || !b.url) return false;
      const track = currentTrackRef.current;
      if (!track || b.trackId !== track.id) return false;
      if ((audio.currentSrc || audio.src) === b.url) return false;   // already on it

      const at = audio.currentTime || 0;
      const onMeta = () => {
        audio.removeEventListener('loadedmetadata', onMeta);
        try { audio.currentTime = at; } catch {}
        audio.play().catch(() => {});
      };
      deliberatePauseRef.current = true;
      audio.addEventListener('loadedmetadata', onMeta);
      audio.src = b.url;
      audio.load();
      console.info('[player] network ran dry, finishing this track from the listen buffer');
      return true;
    };

    // Reopen the stream where the listener was.
    //
    // Position is read BEFORE src is touched, because assigning src resets
    // currentTime to zero and the track would restart. A track already being
    // served from IndexedDB is left alone: a local read does not fail for
    // network reasons, and reloading it would only throw away its buffer.
    const reloadStream = () => {
      const src = audio.currentSrc || audio.src;
      if (!src || src.startsWith('blob:') || src.includes('/offline-audio/')) return false;

      const at = streamResumeAtRef.current || audio.currentTime || 0;
      const wantedPlaying = !audio.paused || interruptedRef.current;

      const onMeta = () => {
        audio.removeEventListener('loadedmetadata', onMeta);
        // A seek past the end of a shorter reload would throw, and a seek we
        // cannot make is not worth losing playback over.
        try {
          if (at > 0 && (!audio.duration || at < audio.duration)) audio.currentTime = at;
        } catch { /* carry on from wherever it landed */ }
        if (wantedPlaying) audio.play().catch(() => { /* the play button is right there */ });
      };

      // Same reason as the swap: assigning src pauses the element, and onPause
      // would otherwise read that as the phone taking the audio.
      deliberatePauseRef.current = true;
      audio.addEventListener('loadedmetadata', onMeta);
      audio.src = src;
      audio.load();
      console.info('[player] reopening the stream at', Math.round(at), 'seconds');
      return true;
    };

    // Buffer first, because its bytes are already here. Only if there is no
    // buffer does this go back to the network. While the device says it is
    // offline there is no point reopening a stream into a dead connection, so
    // it waits for the online event below, which is a real signal rather than
    // a guess.
    const recover = () => {
      if (swapToBuffer()) return;
      if (navigator.onLine === false) { setIsBuffering(true); return; }
      reloadStream();
    };

    const onError = () => {
      const code = audio.error?.code;
      // 4 is SRC_NOT_SUPPORTED: the file is wrong, not the network, and
      // retrying it would spin forever. Hand over to the next track rather
      // than leaving the queue stopped on a track that can never play.
      if (code === 4) {
        clearStallTimer();
        if (!swapToBuffer()) { setIsPlaying(false); playNextFnRef.current?.(); }
        return;
      }
      recover();
    };

    const onStall = () => {
      if (audio.ended) return;
      // Remembered on every stall, so whatever recovers later knows where the
      // listener actually was rather than where the element claims to be after
      // a failed load.
      streamResumeAtRef.current = audio.currentTime || 0;
      setIsBuffering(true);
      if (stallTimerRef.current) return;
      stallTimerRef.current = setTimeout(() => {
        stallTimerRef.current = null;
        recover();
      }, STALL_GRACE_MS);
    };

    // The connection came back. Only acts if this element is actually stuck:
    // a track that kept playing from its buffer must not be interrupted.
    const onOnline = () => {
      if (!audio.src) return;
      const stuck = Boolean(audio.error) || stallTimerRef.current || audio.readyState < 3;
      if (!stuck) return;
      clearStallTimer();
      recover();
    };

    // Going offline is not a failure on its own: a track with enough buffered
    // plays on to the end of what it has. This only stops the timer from
    // reopening a stream into a connection known to be down.
    const onOffline = () => {
      streamResumeAtRef.current = audio.currentTime || 0;
      if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    };

    audio.addEventListener('error',      onError);
    audio.addEventListener('stalled',    onStall);
    audio.addEventListener('waiting',    onStall);
    audio.addEventListener('playing',    clearStallTimer);
    audio.addEventListener('timeupdate', clearStallTimer);
    window.addEventListener('online',    onOnline);
    window.addEventListener('offline',   onOffline);

    return () => {
      clearStallTimer();
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
      audio.removeEventListener('error',      onError);
      audio.removeEventListener('stalled',    onStall);
      audio.removeEventListener('waiting',    onStall);
      audio.removeEventListener('playing',    clearStallTimer);
      audio.removeEventListener('timeupdate', clearStallTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentTime >= 30 && !streamLoggedRef.current && currentTrack) {
      streamLoggedRef.current = true;
      logStream(currentTrack.id);
    }
  }, [currentTime, currentTrack?.id]);

  // Send up whatever was played offline. Once on mount, and every time the
  // connection comes back.
  //
  // The flush is safe to run as often as it likes: each queued play carries
  // the id the server keys its receipt on, so a play already counted is
  // recognised and dropped rather than counted again.
  useEffect(() => {
    let cancelled = false;
    const flush = () => {
      if (!navigator.onLine) return;
      flushOfflinePlays(supabase)
        .then(({ flushed, counted }) => {
          if (cancelled || !flushed) return;
          console.info(`[offline] sent ${flushed} queued play(s), ${counted} counted`);
        })
        .catch(() => { /* the next reconnect tries again */ });
    };
    // A moment after load, so it never competes with getting audio playing.
    const t = setTimeout(flush, 4000);
    window.addEventListener('online', flush);
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener('online', flush);
    };
  }, []);

  const logStream = async (trackId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || null;
      if (!userId) return;

      // ── Offline plays ─────────────────────────────────────────────────────
      // With no connection every call below fails and this function returns
      // early, which is how offline listening would silently stop counting
      // plays: eight listens on a flight, zero streams, and an artist not
      // paid for music that was actually played.
      //
      // So the play is written to a durable queue with an id generated here,
      // and flushed when there is a network again. log_offline_stream
      // (migration 104) keys on that id, so a retry cannot count the same
      // play twice.
      //
      // Queued ONLY when the browser says it is offline, which is the one
      // case where we know for certain the request never left the device. A
      // "failed to fetch" while apparently online is ambiguous, it may have
      // landed and lost its response, and queueing that would risk
      // double-counting, which is worse than losing it. That case keeps
      // today's behaviour until log_stream itself takes a client id.
      if (!navigator.onLine) {
        queueOfflinePlay(buildOfflinePlayRow({
          trackId,
          durationPlayed: Math.floor(audioRef.current.currentTime),
          source: playSourceRef.current || 'offline',
        }));
        return;
      }

      // 1. Fetch track title up front (still needed for notification copy below)
      const { data: track } = await supabase
        .from('tracks')
        .select('artist_id, title')
        .eq('id', trackId)
        .single();

      if (!track) return;

      // 2-4. Atomic RPC: inserts the stream row, increments tracks.stream_count,
      // increments artists.total_streams (+ collab artists), and self-stream
      // guard, all in a single Postgres transaction.
      const { data: logResult, error: logError } = await supabase.rpc('log_stream', {
        p_track_id: trackId,
        p_user_id: userId,
        p_duration_played: Math.floor(audioRef.current.currentTime),
        // Ignored by log_stream now, which always inserts false and lets
        // finalise_stream write the real value. Passed only because the
        // function signature still takes it.
        p_completed: false,
        p_platform: 'web',
        p_device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        p_source: playSourceRef.current || 'unknown',
      });

      if (logError || !logResult?.logged) return; // self-stream or track not found, bail like before

      // Remember which row this play wrote, so finalise_stream can correct
      // it with the real duration and completion when the play ends.
      currentStreamIdRef.current = logResult.stream_id || null;

      // The recipient used to be resolved here from logResult.owner_user_id and
      // passed as user_id on a direct insert. Both notifications below now go
      // through an RPC that resolves the artist's user itself, so there is
      // nothing left for the client to look up.

      // 4b. first_listener, fire once when stream_count goes from 0 to 1
      // prior_stream_count is the BEFORE value, computed server-side inside the same transaction
      if (logResult.prior_stream_count === 0) {
        try {
          const { data: fullFirst } = await supabase
            .from('tracks')
            .select('title, slug, cover_artwork_url, file_url')
            .eq('id', trackId)
            .maybeSingle();
          // Fetch the listener's display name
          const { data: listenerArtist } = await supabase
            .from('artists')
            .select('id, artist_name, profile_image_url, slug')
            .eq('user_id', userId)
            .maybeSingle();
          const listenerName = listenerArtist?.artist_name || 'Someone';
          // Through send_notification, not a direct insert. The INSERT policy on
          // notifications permits only self-addressed rows, and this one is
          // addressed to the ARTIST by a listener, which is why it has been
          // returning 403 on every first stream since it was written. See
          // migration 106.
          await sendNotification(supabase, 'first_listener (player)', {
            type:      'first_listener',
            artistId:  track.artist_id,
            title:     `🎯 First ever stream on ${fullFirst?.title || track.title}`,
            message:   `${listenerName} was your very first listener.`,
            trackId:   trackId,
            metadata: {
              from_artist_id:    listenerArtist?.id || null,
              track_id:          trackId,
              track_title:       fullFirst?.title || track.title,
              track_slug:        fullFirst?.slug || null,
              track_artwork:     fullFirst?.cover_artwork_url || null,
              file_url:          fullFirst?.file_url || null,
              from_artist_name:  listenerName,
              from_artist_image: listenerArtist?.profile_image_url || null,
              from_artist_slug:  listenerArtist?.slug || null,
            },
          });
        } catch { /* non-critical */ }
      }

      // 5. Notify artist of new stream (rich notification with track info)
      // DB trigger was dropped, we create it here so we control the metadata
      try {
        const { data: fullTrack } = await supabase
          .from('tracks')
          .select('title, cover_artwork_url, file_url, album_id, albums(title, cover_artwork_url)')
          .eq('id', trackId)
          .maybeSingle();

        // Group album tracks, only notify once per album session
        // Use track's album_id to determine title/artwork to show
        const isAlbumTrack = !!fullTrack?.album_id;
        const notifTitle   = isAlbumTrack
          ? `New stream on ${fullTrack.albums?.title || fullTrack.title}`
          : `New stream on ${fullTrack?.title}`;
        const notifArtwork = fullTrack?.cover_artwork_url
          || fullTrack?.albums?.cover_artwork_url;

        // Daily digest: one stream summary per artist per day
        // Count today's streams for this artist and upsert a single notification
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStartISO = todayStart.toISOString();

        // Count today's total streams for this artist
        const { count: todayStreams } = await supabase
          .from('streams')
          .select('*', { count: 'exact', head: true })
          .eq('artist_id', track.artist_id)
          .gte('created_at', todayStartISO);

        // Find today's top track by stream count
        const { data: topTrackData } = await supabase
          .from('streams')
          .select('track_id, tracks(title, slug, cover_artwork_url, file_url)')
          .eq('artist_id', track.artist_id)
          .gte('created_at', todayStartISO);

        // Count per track
        const trackCounts = {};
        (topTrackData || []).forEach(s => {
          trackCounts[s.track_id] = (trackCounts[s.track_id] || 0) + 1;
        });
        const topTrackId = Object.entries(trackCounts).sort((a,b) => b[1]-a[1])[0]?.[0];
        const topTrack   = topTrackData?.find(s => s.track_id === topTrackId)?.tracks;

        const streamCount = todayStreams || 1;
        const topTitle    = topTrack?.title || fullTrack?.title;
        const digestTitle = streamCount === 1
          ? `First stream today on ${topTitle}`
          : `${streamCount} stream${streamCount > 1 ? 's' : ''} today, ${topTitle} leading`;

        // One call, and the server decides create-or-update.
        //
        // This used to be a read, a decision and a write from the client, and
        // BOTH branches failed silently: the insert on the INSERT policy and
        // the update on the UPDATE policy, because the row belongs to the
        // artist while the person streaming is a listener. Every error sat
        // inside a catch that never read it, so an artist's stream
        // notifications simply never arrived.
        //
        // Doing it server-side also closes the race: two listeners streaming
        // in the same second could each find no digest and each create one.
        await sendStreamDigest(supabase, 'stream digest (player)', {
          artistId: track.artist_id,
          title:    digestTitle,
          message:  `${streamCount} stream${streamCount !== 1 ? 's' : ''} across your catalogue today`,
          trackId:  topTrackId || trackId,
          metadata: {
            track_id:      topTrackId || trackId,
            track_slug:    topTrack?.slug || fullTrack?.slug || null,
            track_title:   topTitle,
            track_artwork: topTrack?.cover_artwork_url || notifArtwork,
            file_url:      topTrack?.file_url || fullTrack?.file_url,
            artist_id:     track.artist_id,
            stream_count:  streamCount,
          },
        });
      } catch { /* non-critical, never break playback */ }

      // 5b. Fan milestone, celebrate the LISTENER's loyalty to this artist.
      //     "You've played [Artist] 100 times", fires at 10, 50, 100, 250, 500, 1000.
      const FAN_MILESTONES = [10, 50, 100, 250, 500, 1000];
      try {
        const { count: totalPlays } = await supabase
          .from('streams')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .in('track_id',
            // Get all track IDs by this artist so we count artist-level plays
            (await supabase.from('tracks').select('id').eq('artist_id', track.artist_id))
              .data?.map(t => t.id) || [trackId]
          );

        if (FAN_MILESTONES.includes(totalPlays)) {
          const { data: artistInfo } = await supabase
            .from('artists').select('artist_name').eq('id', track.artist_id).maybeSingle();
          const name = artistInfo?.artist_name || 'this artist';

          const milestoneMessages = {
            10:   { title: `10 plays with ${name}`, message: `You keep coming back. That's what being a real fan looks like.` },
            50:   { title: `50 plays with ${name}`, message: `Fifty plays in. You clearly know something others don't.` },
            100:  { title: `100 plays with ${name} 🎯`, message: `One hundred plays. You're not just a listener, you're a supporter.` },
            250:  { title: `250 plays with ${name}`, message: `250 plays deep. The artist notices fans like you.` },
            500:  { title: `500 plays with ${name} 🔥`, message: `500 plays. That's dedication. Top fan energy.` },
            1000: { title: `1000 plays with ${name} 🏆`, message: `A thousand plays. Legendary listener status. This artist owes you one.` },
          };

          const msg = milestoneMessages[totalPlays];
          if (msg) {
            await supabase.from('notifications').insert({
              user_id:  userId,
              type:     'top_supporter',
              title:    msg.title,
              message:  msg.message,
              metadata: { artist_id: track.artist_id, artist_name: name, play_count: totalPlays, fan_milestone: true },
            });
          }
        }
      } catch { /* fan milestone is non-critical, never let it break playback */ }

      // 6. Collab artist increments now handled atomically inside log_stream() above.
    } catch (err) {
      console.error('Failed to log stream:', err);
    }
  };

  const playTrack = useCallback((track, trackList = []) => {
    if (!track?.file_url) return;

    // THE GATE. Every path into playback comes through here, feeds, rails,
    // queues, action sheets, notifications, radio, so this is the only place
    // the rule has to be written, and no new call site can forget it.
    //
    // It blocks only on positive evidence (see trackAccess.js): an explicit
    // is_published === false, or a pre-order with a future release_date and no
    // entitlement. A missing field is unknown, and unknown plays.
    const availability = getTrackAvailability(track);
    if (!availability.playable) {
      console.warn(`[player] blocked playback of "${track.title}" (${track.id}):`, availability.reason);
      setPlaybackNotice({ id: `${track.id}:${Date.now()}`, message: availability.message });
      return;
    }

    const audio = audioRef.current;
    if (currentTrack?.id === track.id) {
      if (isPlaying) { deliberatePauseRef.current = true; audio.pause(); }
      else { interruptedRef.current = false; audio.play().catch(console.error); }
      // Does NOT un-minimise. Tapping the playing track is play/pause, and
      // forcing the full player back open made it impossible to keep it
      // minimised.
      return;
    }
    flushListeningEvent('track_change');
    // AFTER the flush, never before. flushListeningEvent writes the event for
    // the play that is ending, and that play belongs to the screen it started
    // on. Capturing above this line would have stamped the outgoing play with
    // the incoming track's source, which is the same drift this is fixing.
    playSourceRef.current = resolvePlaySource();
    entryFromRef.current = readExternalEntry();
    streamLoggedRef.current = false;
    // Ours, not the phone's. Without this the pause below reads as an
    // interruption and the resume logic would fight the track change.
    deliberatePauseRef.current = true;
    interruptedRef.current     = false;
    audio.pause();
    audio.dataset.feelzTrackId = String(track.id);
    audio.volume = 0;
    // On a warm cache this assigns and loads before yielding, exactly as the
    // old `audio.src = playbackSrc(track)` did. On a cold cache with
    // REACT_APP_PRIVATE_AUDIO on it waits for a signed URL instead of
    // assigning a public file_url that no longer resolves once the bucket is
    // private. The canplay listener is attached below either way, so it fires
    // whenever the source actually lands.
    assignPlaybackSrc(audio, track).then(() => {
      resolveLocalLater(audio, track);
      // Swaps in a short lived signed URL once one is available, so file_url
      // stops being a permanent public link to the master audio. No-op unless
      // REACT_APP_PRIVATE_AUDIO is set. See utils/streamUrl.js.
      resolveStreamLater(audio, track);
    });
    const playWhenReady = () => {
      audio.play().catch(() => {});
      // FADE TO WHAT THEY SET, NOT TO HALF OF IT.
      //
      // This faded in to volumeRef.current * 0.5 and stopped there, so every
      // track started by playTrack played at half volume for its whole
      // length. Nothing told anybody: the slider is driven by React state,
      // which this never touched, so it sat at 100% while the audio element
      // was on 0.5. Then the first touch of the slider called
      // setVolumeLevel, which writes the slider's own value straight to the
      // element, and the sound jumped. That is the "it snaps back to 100".
      //
      // The crossfade path in playNextFromRef never did this: it fades to
      // volumeRef.current, full. So the same song was one loudness when it
      // came up in the queue and half that when you tapped it.
      //
      // The intent was that a track should not start shouting, and the fade
      // is what does that. Ending it below where the person set their volume
      // is a second, silent decision, and not one the UI could show.
      const targetVol = volumeRef.current;
      const steps      = 24;
      const durationMs = 1400;
      const interval   = durationMs / steps;
      let step = 0;
      const fadeIn = setInterval(() => {
        step++;
        audio.volume = Math.min(targetVol, targetVol * (step / steps));
        if (step >= steps) clearInterval(fadeIn);
      }, interval);
      audio.removeEventListener('canplay', playWhenReady);
    };
    audio.addEventListener('canplay', playWhenReady);
    setCurrentTrack(track);
    fillArtistName(track);
    preloadCover(track);
    setCurrentTime(0);
    // Deliberately not setIsMinimized(false). This fired on every track
    // change, so the panel reopened itself as soon as the next song began
    // and the minimise button looked broken. The mini player's own tap
    // handler is the explicit way back in.
    // ── THE QUEUE, AND THE TWO WAYS IT USED TO GO WRONG ──────────────────────
    //
    // 1. A TRACK THAT IS NOT IN THE LIST IT WAS PLAYED WITH.
    //    findIndex returned -1 and the old code read that as 0. So the queue
    //    index pointed at the first track of the list while something else
    //    was playing, and "next" meant the second item of a list the playing
    //    track was never part of. On an artist page that is every tap outside
    //    the main track list: the top pick, a collaboration, an album row.
    //    Whatever happened next was wrong, and if the list held one item it
    //    was the end of the queue, so nothing happened at all.
    //
    // 2. AN EMPTY LIST LEFT THE OLD QUEUE IN PLACE.
    //    The whole block was skipped, so the previous screen's queue and its
    //    index survived under a track from this one. Next played something
    //    from wherever you had been before, or nothing, depending on where
    //    that stale index happened to sit.
    //
    // Both are fixed the same way: the playing track is ALWAYS in the queue
    // and the index always points at it. When it is not in the list it is put
    // at the front, so next is the rest of that list rather than a guess.
    const inList = trackList.findIndex(t => t.id === track.id);
    let nextQueue;
    let resolvedIdx;
    if (inList >= 0) {
      nextQueue = trackList;
      resolvedIdx = inList;
    } else if (trackList.length > 0) {
      nextQueue = [track, ...trackList.filter(t => t.id !== track.id)];
      resolvedIdx = 0;
    } else {
      nextQueue = [track];
      resolvedIdx = 0;
    }
    setQueue(nextQueue);
    setQueueIndex(resolvedIdx);
    queueRef.current = nextQueue;
    queueIndexRef.current = resolvedIdx;
    // Preload next track's cover too
    const nextIdx = resolvedIdx + 1;
    if (nextIdx < nextQueue.length) preloadCover(nextQueue[nextIdx]);
  }, [currentTrack, isPlaying, flushListeningEvent]);

  // A one-line notice anyone can raise.
  //
  // Built for blocked playback, but it is the only app-wide notice renderer
  // that exists, and `window.showToast` was referenced in
  // utils/downloadTrack.js while being defined absolutely nowhere, so the
  // iOS "Tap Share to save" hint has never appeared. Publishing showNotice
  // there makes that dead reference work and gives plain utils, which have no
  // access to React context, a way to say something.
  const showNotice = useCallback((message) => {
    if (!message) return;
    setPlaybackNotice({ id: `notice:${Date.now()}`, message });
  }, []);

  useEffect(() => {
    window.showToast = showNotice;
    return () => { if (window.showToast === showNotice) window.showToast = null; };
  }, [showNotice]);

  // The notice clears itself. Tying it to a dismiss button would mean every
  // page that can play a track also has to render one.
  useEffect(() => {
    if (!playbackNotice) return;
    const t = setTimeout(() => setPlaybackNotice(null), 4000);
    return () => clearTimeout(t);
  }, [playbackNotice]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (isPlaying) {
      // Marked before the call, because the pause event fires synchronously
      // in some browsers and would otherwise read as an interruption.
      deliberatePauseRef.current = true;
      interruptedRef.current     = false;
      audio.pause();
      // Someone who pressed pause has finished with the speakers. Holding the
      // claim here would keep another app quiet for no reason.
      releaseAudioSession();
    } else {
      interruptedRef.current = false;
      claimAudioSession();
      audio.play().catch(console.error);
    }
  }, [isPlaying]);

  const seek = useCallback((time) => {
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  // Driving mode sets this while its screen is open. Nothing else should.
  //
  // useCallback and not an inline arrow in the value object, because the
  // driving screen turns this on in an effect keyed on this function. A fresh
  // identity every render would tear the effect down and set it up again on
  // every tick of the progress bar, which means driving mode switching itself
  // off and on several times a second.
  const setDrivingMode = useCallback((on) => {
    drivingRef.current = !!on;
    if (on && !audioRef.current.paused) claimAudioSession();
  }, []);

  const setVolumeLevel = useCallback((v) => {
    audioRef.current.volume = v;
    setVolume(v);
    volumeRef.current = v;
  }, []);

  const playNext = useCallback(() => { playNextFromRef(); }, [playNextFromRef]);

  const playPrev = useCallback(() => {
    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      return;
    }
    const q   = queueRef.current;
    const idx = queueIndexRef.current;
    if (q.length === 0) return;
    let prevIndex = idx - 1;
    if (prevIndex < 0) {
      if (repeatRef.current === 'all') { prevIndex = q.length - 1; }
      else { audioRef.current.currentTime = 0; return; }
    }
    const prevTrack = q[prevIndex];
    if (prevTrack?.file_url) {
      streamLoggedRef.current = false;
      audioRef.current.pause();
      audioRef.current.dataset.feelzTrackId = String(prevTrack.id);
      audioRef.current.volume = volumeRef.current;
      assignPlaybackSrc(audioRef.current, prevTrack).then(() => {
        resolveLocalLater(audioRef.current, prevTrack);
        resolveStreamLater(audioRef.current, prevTrack);
      });
      const playPrevWhenReady = () => {
        audioRef.current.play().catch(() => {});
        audioRef.current.removeEventListener('canplay', playPrevWhenReady);
      };
      audioRef.current.addEventListener('canplay', playPrevWhenReady);
      setCurrentTrack(prevTrack);
      setQueueIndex(prevIndex);
      setCurrentTime(0);
    }
  }, []);

  const addToQueue      = useCallback((track) => { setQueue(prev => [...prev, track]); }, []);
  const removeFromQueue = useCallback((index) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
    setQueueIndex(prev => index < prev ? prev - 1 : prev);
  }, []);
  const moveInQueue = useCallback((from, to) => {
    setQueue(prev => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, []);
  const playNextInQueue = useCallback((track) => {
    setQueue(prev => {
      const next = [...prev];
      next.splice(queueIndexRef.current + 1, 0, track);
      return next;
    });
  }, []);
  const clearQueue    = useCallback(() => { setQueue([]); setQueueIndex(-1); }, []);

  // Dismiss the player entirely.
  //
  // Nothing in the app set currentTrack back to null, and MiniPlayer /
  // DesktopPlayer only return null when it is null, so once you played
  // anything, the bar was permanent for the rest of the session with no
  // control to get rid of it.
  //
  // Closing stops the music rather than just hiding the bar: a hidden player
  // still playing, with no way to reach the pause button, is worse than no
  // close button at all.
  //
  // The real listen is flushed first so the stream row is finalised honestly
  // (migration 79), and the stream id is cleared so this play can never be
  // written onto the next track's row.
  const closePlayer = useCallback(() => {
    // 'track_change' rather than a new 'closed' value: end_reason may carry a
    // CHECK constraint in the database, and a rejected insert would be
    // swallowed by the try/catch in flushListeningEvent AND skip
    // finalise_stream with it. Abandoning a play is what a close is, and
    // completed stays false either way. Worth a distinct reason later, once
    // the constraint on listening_events.end_reason has been checked.
    flushListeningEvent('track_change');

    [audioRef.current, audioRefB.current].forEach(a => {
      if (!a) return;
      try {
        a.pause();
        a.removeAttribute('src');   // stop the download, not just the sound
        a.load();
      } catch { /* an already-torn-down element is not worth failing over */ }
    });

    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setQueue([]);
    setQueueIndex(-1);
    queueRef.current = [];
    queueIndexRef.current = -1;
    setIsMinimized(true);
    setCurrentTrack(null);
    currentTrackRef.current = null;
  }, [flushListeningEvent]);
  const toggleShuffle = useCallback(() => setShuffle(prev => !prev), []);
  const toggleRepeat  = useCallback(() => {
    setRepeat(prev => {
      if (prev === 'none') return 'one'; // none → one (repeat current track)
      if (prev === 'one')  return 'all'; // one  → all (repeat queue)
      return 'none';                     // all  → none
    });
  }, []);

  // Replaces the queue and index without re-triggering playback.
  // Used by pages that start playback immediately then resolve the full queue async.
  const replaceQueue = useCallback((list, idx) => {
    setQueue(list);
    queueRef.current = list;
    setQueueIndex(idx);
    queueIndexRef.current = idx;
  }, []);

  // Jump to a specific index in the current queue and start playing
  const jumpToIndex = useCallback((idx) => {
    const q = queueRef.current;
    if (!q || idx < 0 || idx >= q.length) return;
    const track = q[idx];
    if (!track) return;
    const audio = audioRef.current;
    audio.pause();
    audio.dataset.feelzTrackId = String(track.id);
    audio.volume = volumeRef.current;
    // play() has to wait for the source here, because unlike the paths above
    // there is no canplay listener already attached to catch it later.
    assignPlaybackSrc(audio, track).then(() => {
      resolveLocalLater(audio, track);
      resolveStreamLater(audio, track);
      audio.play().catch(() => {
        audio.load();
        const onReady = () => { audio.play().catch(() => {}); audio.removeEventListener('canplay', onReady); };
        audio.addEventListener('canplay', onReady);
      });
    });
    setCurrentTrack(track);
    setQueueIndex(idx);
    queueIndexRef.current = idx;
    setCurrentTime(0);
    streamLoggedRef.current = false;
    setIsMinimized(false);
  }, []); // eslint-disable-line

  // Expose replaceQueue globally so async callbacks (e.g. notification taps)
  // can patch the queue after playback has already started
  React.useEffect(() => {
    window.__feelz_replaceQueue = (list, idx) => {
      setQueue(list);
      queueRef.current = list;
      if (idx !== undefined) { setQueueIndex(idx); queueIndexRef.current = idx; }
    };
    return () => { window.__feelz_replaceQueue = null; };
  }, []); // eslint-disable-line

  const value = {
    // True while the audio has stalled waiting for data, so the bar can say
    // buffering instead of claiming the track is playing in silence.
    isBuffering,
    currentTrack, isPlaying, duration, currentTime, volume, queue, queueIndex,
    shuffle, repeat, isMinimized, setIsMinimized, desktopPanelView, setDesktopPanelView, playTrack, togglePlay, seek,
    setVolume: setVolumeLevel, setVolumeLevel, playNext, playPrev, addToQueue,
    removeFromQueue, moveInQueue, playNextInQueue, clearQueue, closePlayer, toggleShuffle, toggleRepeat,
    replaceQueue, jumpToIndex,
    playbackNotice, showNotice, dismissPlaybackNotice: () => setPlaybackNotice(null),
    setDrivingMode,
  };

  return (
    <PlayerProviderInner
      value={value}
      currentTrack={currentTrack}
      isPlaying={isPlaying}
      togglePlay={togglePlay}
      playNext={playNext}
      playPrev={playPrev}
      seek={seek}
      currentTime={currentTime}
      duration={duration}
    >
      {children}
      {/* The blocked-playback notice, rendered by the provider so it exists
          wherever the player does. Above the mini player and the mobile nav,
          below modals. */}
      {playbackNotice?.message && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[420] px-4 w-full max-w-sm pointer-events-none"
          style={{ bottom: 'calc(112px + var(--safe-area-bottom, 0px))' }}
        >
          <div
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl shadow-2xl pointer-events-auto"
            style={{
              backgroundColor: 'rgba(20,20,28,0.96)',
              border: '1px solid rgba(251,191,36,0.28)',
              backdropFilter: 'blur(12px)',
            }}
            onClick={() => setPlaybackNotice(null)}
          >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#fbbf24' }} />
            <p className="text-xs text-white/80">{playbackNotice.message}</p>
          </div>
        </div>
      )}
    </PlayerProviderInner>
  );
}

export const usePlayer = () => useContext(PlayerContext);